import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import YesNoCell from '../../../../../components/tables/cellComponents/YesNoCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetAllOrganizationsQuery } from '../../../../../services/endpoints/places/organization';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { useGetCoursesSimpleQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCLocation, SFCVenue } from '../../../../../types/place.types';
import { getFilterValue, parseVenuesDeepLinkFilters } from '../../../../../utils/listFilterSearchParams';
import { formatDateString } from '../../../../../utils/utils';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { canViewVenues } from '../../../../../utils/rbac';
import { isGlobal } from '../../../../../utils/consts';

interface DataRow extends SFCVenue {}

// =============================================================================
// COMPONENT
// =============================================================================

export const VenuesView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userHasAccess = canViewVenues(currentUser);
  const isGlobalUser = currentUser ? isGlobal(currentUser.user_type_id) : false;

  const [filters, setFilters] = useListFiltersInUrl(
    { scope: 'all', data: { page: 1, entries: 20 } },
    { omitScopeFromUrl: true, parseExtra: parseVenuesDeepLinkFilters }
  );
  const { data, error, isLoading: venuesLoading, isFetching } = useGetVenuesQuery(filters, {
    skip: !userHasAccess,
    refetchOnMountOrArgChange: true
  });
  const {
    data: organizations,
    error: organizationsError
  } = useGetAllOrganizationsQuery(
      { data: { entries: 10000, orderBy: { field: 'name', order: 'asc' } } },
      { skip: !userHasAccess }
  );
  const {
    data: courses,
    error: coursesError
  } = useGetCoursesSimpleQuery(
      { data: { orderBy: { field: 'title', order: 'asc' } } },
      { skip: !userHasAccess }
  );

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Title',
      value: getFilterValue(filters.data?.filterBy, 'title')
    },
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    ...(isGlobalUser
        ? [
          {
            name: 'location_id',
            label: 'Location',
            type: 'number' as const,
            element: (
                <FormSelect
                    name="location_id"
                    label="Location"
                    options={locationOptions}
                    selected_Value={locationOptions.find(
                        (option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'location_id')
                    )}
                />
            )
          }
        ]
        : []),
    {
      name: 'organization_id',
      label: 'Organization',
      type: 'number',
      element: (
          <FormSelect
              name="organization_id"
              label="Organization"
              options={organizations?.results.map((i) => ({ value: i.id, label: i.name })) ?? []}
              loadingError={organizationsError ? 'Error loading organizations' : undefined}
              selected_Value={organizations?.results
                  ?.map((i) => ({ value: i.id, label: i.name }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'organization_id'))}
          />
      )
    },
    {
      name: 'course_id',
      label: 'Session',
      type: 'number',
      element: (
          <FormSelect
              name="course_id"
              label="Session"
              options={courses?.results.map((r) => ({ value: r.id, label: r.title })) ?? []}
              loadingError={coursesError ? 'Error loading courses' : undefined}
              selected_Value={courses?.results
                  ?.map((r) => ({ value: r.id, label: r.title }))
                  .find((option) => String(option.value) === getFilterValue(filters.data?.filterBy, 'course_id'))}
          />
      )
    },
    {
      name: 'phone',
      label: 'Phone',
      value: getFilterValue(filters.data?.filterBy, 'phone')
    },
    {
      name: 'address',
      label: 'Address',
      value: getFilterValue(filters.data?.filterBy, 'address')
    },
    {
      name: 'city',
      label: 'City',
      value: getFilterValue(filters.data?.filterBy, 'city')
    },
    {
      name: 'state',
      label: 'State',
      value: getFilterValue(filters.data?.filterBy, 'state')
    },
    {
      name: 'zip',
      label: 'Zip',
      value: getFilterValue(filters.data?.filterBy, 'zip')
    },
    {
      name: 'ends_on',
      id: 'date_from',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="date_from"
          label="Last Session End Date Start"
        />
      )
    },
    {
      name: 'ends_on',
      id: 'date_to',
      operator: 'lesser_equal_than',
      element: (
        <DateFilterField
          name="date_to"
          label="Last Session End Date End"
        />
      )
    }
  ];

  const columns: ColumnDef<DataRow>[] = [
    {
      header: 'ID',
      accessorKey: 'id'
    },
    {
      header: 'Title',
      accessorKey: 'title'
    },
    {
      header: 'Organizations',
      accessorKey: 'organizations',
      enableSorting: false,
      cell: ({ row }) => (
          <>
            {row.original.organizations?.map((o) => (
                <React.Fragment key={o.id}>
                  <LinkCell content={o.name} url={`/admin/places/organizations/${o.id}`} />
                  <br />
                </React.Fragment>
            ))}
          </>
      )
    },
    {
      header: 'Location',
      accessorKey: 'location',
      enableSorting: false,
      cell: ({ getValue }) => {
        let location = getValue() as SFCLocation;
        return (
            <LinkCell
                content={location?.title ?? 'error'}
                url={`/admin/places/locations/${location?.id ?? 0}`}
            />
        );
      }
    },
    {
      header: 'Address',
      accessorKey: 'address'
    },
    {
      header: 'City',
      accessorKey: 'city'
    },
    {
      header: 'State',
      accessorKey: 'state'
    },
    {
      header: 'Last Session End Date',
      accessorKey: 'ends_on',
      cell: ({ getValue }) => formatDateString(getValue<any>())
    },
    {
      header: 'Is Active',
      accessorKey: 'is_active',
      cell: ({ getValue }) => <YesNoCell value={getValue<boolean>()} />
    },
    {
      header: '',
      accessorKey: 'blank',
      enableSorting: false,
      cell: ({ row }) => (
          <>
            <LinkCell content="View" url={`/admin/places/venues/${row.original.id}`} />
            &nbsp; &nbsp;
            <LinkCell content="Edit" url={`/admin/places/venues/${row.original.id}/edit`} />
          </>
      )
    }
  ];

  // Handle API errors with better messaging
  useEffect(() => {
    if (error) {
      let errorMessage = 'Error while loading venues';

      // Extract error message from API response
      if ('data' in error && typeof error.data === 'object' && error.data !== null) {
        const apiError = error.data as { message?: string };
        if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if ('message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      addToast(errorMessage, 'error');
    }
  }, [error, addToast]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  // Check permissions before rendering
  if (!userHasAccess) {
    return <Forbidden />;
  }

  return (
      <div className="py-2">
        <SFCTable
            name={'Venues'}
            columns={columns}
            defaultScope={filters.scope ?? 'all'}
            data={data?.results ?? []}
            count={data?.count}
            isLoading={venuesLoading}
            isFetching={isFetching}
            indexDownloadPath="venues"
            filters={filters?.data?.filterBy}
            page_Size={filters?.data?.entries}
            filterForm={<FilterForm fields={filterFormFields} onApplyFilters={setFilterBy} />}
            onFiltersChanged={setFilters}
            newItemEl={
              <SimpleButton variant="sfc-blueberry" height={40} truncate={false} onClick={() => navigate(`/admin/places/venues/new`)}>
                New venue
              </SimpleButton>
            }
        />
      </div>
  );
};
